import React, { useRef, useEffect, useState } from 'react';
import { PDFViewer, PDFLinkService } from 'pdfjs-dist/web/pdf_viewer';
import { PdfWordsLayer } from './PdfWordsLayer';
import { PdfResultsLayer } from './PdfResultsLayer';
import { PdfFile } from '../../types';
import { Label } from '../../types/label';
import { Position, ResultField } from '../../types/result';
import { PDFDocumentProxy } from 'pdfjs-dist';

const getAllElements = (
  scrollEl: HTMLElement,
  views: { div: HTMLDivElement; id: number }[],
  sortByVisibility: boolean = false
) => {
  let top = scrollEl.scrollTop,
    bottom = top + scrollEl.clientHeight;
  let visible = [],
    view,
    element;
  let currentHeight, viewHeight, hiddenHeight, percentHeight;
  let currentWidth;

  for (let i = 0, ii = views.length; i < ii; i++) {
    view = views[i];
    element = view.div;
    currentHeight = element.offsetTop + element.clientTop;
    viewHeight = element.clientHeight;
    currentWidth = element.offsetLeft + element.clientLeft;
    hiddenHeight = Math.max(0, top - currentHeight) + Math.max(0, currentHeight + viewHeight - bottom);
    percentHeight = (((viewHeight - hiddenHeight) * 100) / viewHeight) | 0;

    visible.push({
      id: view.id,
      x: currentWidth,
      y: currentHeight,
      view,
      percent: percentHeight,
    });
  }

  let first = visible[0];
  let last = visible[visible.length - 1];

  if (sortByVisibility) {
    visible.sort(function(a, b) {
      let pc = a.percent - b.percent;
      if (Math.abs(pc) > 0.001) {
        return -pc;
      }
      return a.id - b.id; // ensure stability
    });
  }
  return { first, last, views: visible };
};

type Props = {
  readOnly?: boolean;
  showNonExisting?: boolean;
  newLabelAllowed?: boolean;
  pdfDocument: PDFDocumentProxy;
  getWordsContent: (file: PdfFile) => Promise<any>;
  wordsToHighlight: Label[] | ResultField[];
  onScroll: () => any;
  selectedWord: null | Label;
  editWord: null | Label;
  onAddLabel: (position: Position, page: { number: number }, name: string) => any;
  onEditLabel: (position: Position, page: { number: number }) => any;
  onRemoveLabel: (label: Label) => any;
  onReEditLabel: (label: Label) => any;
  onEditThisLabel: (label: Label, position: Position, page: { number: number }) => any;
  onEditCancel: () => any;
  onResetRemovedLabel: (Label: Label) => any;
  withCorrections: boolean;
  publicDemo?: boolean;
};
const PdfView = (props: Props) => {
  const {
    pdfDocument,
    wordsToHighlight,
    onScroll,
    selectedWord,
    editWord,
    readOnly,
    onAddLabel,
    onEditLabel,
    onRemoveLabel,
    onReEditLabel,
    onResetRemovedLabel,
    onEditThisLabel,
    newLabelAllowed,
    withCorrections,
    publicDemo,
  } = props;

  const containerNode = useRef<null | HTMLDivElement>(null);
  const [viewer, setViewer] = useState<null | PDFViewer>(null);
  const [textLayerRendered, setTextLayerRendered] = useState<boolean>(false);

  useEffect(() => {
    const container = containerNode.current;

    const linkService = new PDFLinkService();
    const viewer = new PDFViewer({
      container: container,
      textLayerMode: 1,
      removePageBorders: true,
      linkService: linkService,
    });
    viewer._getVisiblePages = function() {
      return getAllElements(this.container, this._pages, true);
    };

    viewer.setDocument(pdfDocument);
    linkService.setDocument(pdfDocument);
    linkService.setViewer(viewer);

    setViewer(viewer);

    const onTextLayerRendered = () => {
      setTextLayerRendered(true);
    };
    const onScrollHandler = () => {
      onScroll && onScroll();
    };

    document.addEventListener('textlayerrendered', onTextLayerRendered);
    container && container.addEventListener('scroll', onScrollHandler);

    return () => {
      document.removeEventListener('textlayerrendered', onTextLayerRendered);
      document.removeEventListener('scroll', onScrollHandler);
    };
  }, [pdfDocument, onScroll]);

  return (
    <div ref={containerNode} className="PdfHighlighter" id="scroll-container">
      <div className="pdfViewer" />
      {viewer &&
        textLayerRendered &&
        (withCorrections ? (
          <PdfResultsLayer
            viewer={viewer}
            pdfDocument={pdfDocument}
            highlightedWords={wordsToHighlight as ResultField[]}
            selectedWord={selectedWord}
          />
        ) : (
          <PdfWordsLayer
            viewer={viewer}
            container={containerNode.current}
            pdfDocument={pdfDocument}
            highlightedWords={wordsToHighlight as Label[]}
            selectedWord={selectedWord}
            editWord={editWord}
            onAddLabel={onAddLabel}
            onEditLabel={onEditLabel}
            onRemoveLabel={onRemoveLabel}
            onReEditLabel={onReEditLabel}
            onResetRemovedLabel={onResetRemovedLabel}
            onEditThisLabel={onEditThisLabel}
            readOnly={readOnly}
            newLabelAllowed={newLabelAllowed}
            publicDemo={publicDemo}
          />
        ))}
    </div>
  );
};

export default PdfView;
